import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { QuickFillHooks } from '@frontend/api-quick-fill';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { PanelPortal } from '@frontend/panel-engine';
import {
  QuickFillTrayContent,
  resetWaitlistStore,
  WaitListTrayContent,
  WaitlistTrayContext,
  WaitlistTrayContextType,
} from '@frontend/quick-fill';
import { useIsCompactMode } from '@frontend/responsiveness';
import { SurfaceHeader } from '@frontend/surface-header';
import { ContentLoader } from '@frontend/design-system';
import { useTopBarShallowStore } from '../../store/use-top-bar-store';
import { labelMap } from '../../utils/label-map';
import { TopBarPanelContent } from '../panels/top-bar-panel-scaffold';

const QuickFillPanel = (props: WaitlistTrayContextType) => {
  const {
    isLoading: isLoadingFF,
    isWaitlistFFEnabled,
    isWaitlistDirectBookingEnabled,
  } = QuickFillHooks.useGetWaitlistFeatureFlagDetails();
  const { t } = useTranslation('schedule-quick-fill');
  const isCompactMode = useIsCompactMode();
  const subtitle = isWaitlistDirectBookingEnabled
    ? t('Send waitlisted customers a direct booking link to fill last-minute cancellations fast')
    : t(
        'Easily fill last-minute cancellations or openings in your schedule by messaging your customers who need to get in for an appointment as soon as possible.'
      );

  return (
    <PanelPortal enableAnimations id='quickFill' width='medium' onClose={props.onClose}>
      <SurfaceHeader.Root noBorder>
        <SurfaceHeader.Row>
          {isCompactMode && (
            <SurfaceHeader.Start>
              <SurfaceHeader.Icon iconName='caret-left' onClick={props.onClose} />
            </SurfaceHeader.Start>
          )}
          <SurfaceHeader.Title>{labelMap['quickfill']}</SurfaceHeader.Title>
          {!isCompactMode && (
            <SurfaceHeader.Actions>
              <SurfaceHeader.Icon iconName='x' onClick={props.onClose} />
            </SurfaceHeader.Actions>
          )}
        </SurfaceHeader.Row>
        {!isLoadingFF && <SurfaceHeader.Body>{subtitle}</SurfaceHeader.Body>}
      </SurfaceHeader.Root>
      <TopBarPanelContent>
        {isLoadingFF ? (
          <ContentLoader show />
        ) : isWaitlistFFEnabled ? (
          <WaitlistTrayContext.Provider value={props}>
            <WaitListTrayContent />
          </WaitlistTrayContext.Provider>
        ) : (
          <QuickFillTrayContent closeModal={props.onClose} />
        )}
      </TopBarPanelContent>
    </PanelPortal>
  );
};

export const QuickFillPanelWrapper = () => {
  const { isQuickFillOpen, setQuickFillOpen } = useTopBarShallowStore('isQuickFillOpen', 'setQuickFillOpen');
  const { isWaitlistFFEnabled } = QuickFillHooks.useGetWaitlistFeatureFlagDetails();
  const closeQuickFill = useCallback(() => setQuickFillOpen(false), [setQuickFillOpen]);
  const pathName = useLocation().pathname;
  const [waitlistContextInfo, setWaitlistContextInfo] = useState<Omit<WaitlistTrayContextType, 'onClose'>>({});
  const hasTrackedWaitlistViewedRef = useRef(false);

  SchedulerV3.useWaitlistCustomEventListener('schedule:waitlist:set-context', (data) => {
    resetWaitlistStore();
    setWaitlistContextInfo({
      selectedLocationId: data.locationId,
      selectedProviderId: data.providerId,
      selectedDate: data.selectedDate,
      selectedTime: data.selectedTime,
    });
  });

  useEffect(() => {
    if (!isQuickFillOpen) {
      // reset context and store when panel is closed
      setWaitlistContextInfo({});
      resetWaitlistStore();
      hasTrackedWaitlistViewedRef.current = false;
    }
  }, [isQuickFillOpen]);

  useEffect(() => {
    if (isQuickFillOpen && isWaitlistFFEnabled && !hasTrackedWaitlistViewedRef.current) {
      SchedulerV3.trackPendoEvent('waitlist_viewed', { entryPoint: pathName });
      hasTrackedWaitlistViewedRef.current = true;
    }
  }, [isQuickFillOpen, isWaitlistFFEnabled, pathName]);

  if (!isQuickFillOpen) {
    return null;
  }
  return <QuickFillPanel onClose={closeQuickFill} {...waitlistContextInfo} />;
};
